/**
 * Ctrl K 命令面板（topnav 版式）：输订单号 / 团号（产品编码）/ 功能名直达。
 *
 * · 功能：只从「当前用户有权限看到的菜单项」里匹配（visibleNavItems，权限过滤照旧）——没权限的功能不出现。
 * · 订单 / 产品：只调现有查询接口（订单 ``search``、产品 ``q``），后端本来就按权限 / 租户过滤，这里不另加口子。
 * · 订单没有独立详情页路由：跳「订单中心」并带 ``?search=`` 过滤（与顶栏原有的搜索同一个落点）；产品进产品详情。
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useEdition } from "@/theme/useTheme";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { listOrders } from "@/services/orders";
import { listProducts } from "@/services/products";
import { visibleNavItems, type NavItem } from "./navConfig";
import { useNavFlags } from "./useNavFlags";

interface Hit {
  key: string;
  group: "功能" | "订单" | "产品";
  title: string;
  hint?: string;
  to: string;
}

/** 功能名匹配（不区分大小写；中文名 / 英文名 / 路由里的单词都行）。纯函数，单测直接调。 */
export function matchFeatures(items: NavItem[], kw: string): Hit[] {
  const k = kw.trim().toLowerCase();
  if (!k) return [];
  return items
    .filter((it) => !it.sub && (it.labelCn.toLowerCase().includes(k) || it.labelEn.toLowerCase().includes(k) || it.id.includes(k)))
    .map((it) => ({ key: `nav-${it.id}`, group: "功能" as const, title: it.labelCn, hint: it.labelEn, to: it.to }));
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.user?.permissions);
  const edition = useEdition();
  const navFlags = useNavFlags();
  const navFlagsKey = JSON.stringify(navFlags);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => visibleNavItems(permissions, edition, navFlags), [permissions, edition, navFlagsKey]);
  const [kw, setKw] = useState("");
  const [orders, setOrders] = useState<Hit[]>([]);
  const [products, setProducts] = useState<Hit[]>([]);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const canOrders = items.some((i) => i.id === "orders");
  const canProducts = items.some((i) => i.id === "products");

  useEffect(() => {
    if (open) {
      setKw("");
      setOrders([]);
      setProducts([]);
      setActive(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  // 订单 / 产品：输入 ≥ 2 个字符、停顿 250ms 后查（有权限的才查）
  useEffect(() => {
    const k = kw.trim();
    const my = ++seq.current;
    if (!open || k.length < 2) {
      setOrders([]);
      setProducts([]);
      return;
    }
    const t = setTimeout(() => {
      if (canOrders) {
        listOrders({ search: k } as never, { paged: true })
          .then((rows) => {
            if (my === seq.current) setOrders(rows.slice(0, 5).map((o) => ({ key: `o-${o.id}`, group: "订单" as const, title: o.order_no, hint: [o.customer_name, o.product_name].filter(Boolean).join(" · "), to: `/orders?search=${encodeURIComponent(o.order_no)}` })));
          })
          .catch(() => { if (my === seq.current) setOrders([]); });
      }
      if (canProducts) {
        listProducts({ q: k, limit: 5 } as never, { paged: true })
          .then((rows) => {
            if (my === seq.current) setProducts(rows.slice(0, 5).map((p) => ({ key: `p-${p.id}`, group: "产品" as const, title: `${p.short_code || p.code} ${p.name_cn}`, hint: p.code, to: `/products/${p.id}` })));
          })
          .catch(() => { if (my === seq.current) setProducts([]); });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [kw, open, canOrders, canProducts]);

  const hits: Hit[] = useMemo(() => [...matchFeatures(items, kw), ...orders, ...products], [items, kw, orders, products]);
  useEffect(() => setActive(0), [kw]);

  if (!open) return null;
  const go = (h: Hit) => {
    onClose();
    navigate(h.to);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/40 p-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="命令面板" onClick={onClose} data-testid="cmdk">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            ref={input}
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, Math.max(0, hits.length - 1))); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
              else if (e.key === "Enter" && hits[active]) go(hits[active]);
            }}
            placeholder="搜订单号 / 团号 / 功能名…"
            aria-label="命令面板搜索"
            className="flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
          <kbd className="rounded border border-slate-200 px-1.5 text-[11px] text-slate-400">Esc</kbd>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto py-1" role="listbox">
          {kw.trim().length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-400">输入订单号、团号（产品编码）或功能名，回车直达</li>}
          {kw.trim().length > 0 && hits.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-400">没有匹配的功能 / 订单 / 产品</li>}
          {hits.map((h, i) => (
            <li key={h.key} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(h)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm ${i === active ? "bg-brand-soft text-brand" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <span className="w-9 shrink-0 text-[11px] text-slate-400">{h.group}</span>
                <span className="flex-1 truncate font-medium">{h.title}</span>
                {h.hint && <span className="truncate text-xs text-slate-400">{h.hint}</span>}
                {i === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
